import type { Session } from "@supabase/supabase-js";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { authRepository } from "@/infrastructure/auth/auth.repository";

export const AuthState = {
  IDLE: "idle",
  AUTHENTICATING: "authenticating",
  AUTHENTICATED: "authenticated",
  UNAUTHENTICATED: "unauthenticated",
} as const;
export type AuthStateValue = (typeof AuthState)[keyof typeof AuthState];

interface AuthContextValue {
  state: AuthStateValue;
  session: Session | null;
  signIn: (email: string, password: string) => Promise<{ ok: boolean; message: string | null }>;
  signUp: (email: string, password: string) => Promise<{ ok: boolean; message: string | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthStateValue>(AuthState.AUTHENTICATING);
  const [session, setSession] = useState<Session | null>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    let cancelled = false;
    authRepository.getSession().then((s) => {
      if (cancelled) return;
      setSession(s);
      setState(s ? AuthState.AUTHENTICATED : AuthState.UNAUTHENTICATED);
    });
    const unsubscribe = authRepository.onAuthStateChange((s) => {
      setSession(s);
      setState(s ? AuthState.AUTHENTICATED : AuthState.UNAUTHENTICATED);
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const signIn: AuthContextValue["signIn"] = async (email, password) => {
    setState(AuthState.AUTHENTICATING);
    const result = await authRepository.signInWithPassword(email, password);
    if (result.error) {
      setState(AuthState.UNAUTHENTICATED);
      return { ok: false, message: result.error.message };
    }
    return { ok: true, message: null };
  };

  const signUp: AuthContextValue["signUp"] = async (email, password) => {
    setState(AuthState.AUTHENTICATING);
    const result = await authRepository.signUpWithPassword(email, password);
    if (result.error) {
      setState(AuthState.UNAUTHENTICATED);
      return { ok: false, message: result.error.message };
    }
    return { ok: true, message: result.session ? null : "Check your email to confirm sign-up." };
  };

  const signOut = async () => {
    await authRepository.signOut();
    queryClient.clear();
  };

  return (
    <AuthContext.Provider value={{ state, session, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
